import clsx from "clsx";
import { Chip, OutlinedTag } from "./Chip";
import { IncidentStatusChip } from "./IncidentStatusChip";
import { isStepComplete } from "../plan-steps";
import { TileLabel } from "./SectionCard";
import { fmtActor, fmtDate } from "../format";
import {
  MACHINE_STEP_HINT,
  MACHINE_STEP_TAG,
  MANUAL_STEP_HINT,
  MANUAL_STEP_TAG,
  checkLabel,
  humanizeEvidence,
  splitEvidence,
  stepExecutionSummary,
} from "../vocabulary";
import {
  ENGINE_FROM_CLAIM_HINT,
  problemDescription,
  problemSolution,
  setupHint,
  setupSummary,
} from "../problem-copy";
import type { HotelCopyContext } from "../problem-copy";
import type {
  FixStepAction,
  IncidentEventRow,
  IncidentLite,
  PlanProblemView,
  PlanStepView,
} from "../types";

/**
 * ONE problem and its fix, in ONE card.
 *
 * They were two cards in facing grid columns until 2026-08-14, which read as
 * two unrelated lists the moment a plan carried more than one problem —
 * nothing but vertical position said which fix answered which problem, and a
 * tall fix plan broke even that. The pairing is the card now: a header naming
 * the problem, and beneath it the evidence (left) and the steps (right).
 *
 * Copy rules the card carries:
 * - **No `P{n} —` prefix, anywhere.** The problem number is how the catalog
 *   indexes a fix strategy; it tells an operator nothing. Same for the check
 *   id in a finding line — `p3.purchase-absence` is replaced by the check's
 *   English name, with the id left on hover for whoever is reading the code.
 * - **Tags only where they say something.** The header carries the problem's
 *   resolution (`fixed` / `accepted`) or, failing that, the incident's status —
 *   but since 2026-08-17 that chip renders **outcomes only**, so an open
 *   problem under an active incident carries nothing. `Fix identified` and
 *   `Waiting on others` used to sit here restating a phase of the plan whose
 *   steps are rendered an inch below, each with its own `By hand` /
 *   `Automatic` tag beside the button that acts on it. `Fix applied` still
 *   shows, because "done, proving itself, leave it" is not on the card
 *   otherwise. `confirmed` provenance is the norm and renders nothing, while
 *   `inferred` — a plan resting on a cause no check on this hotel observed —
 *   keeps its tag, because that one is a warning.
 * - **A resolved problem stays on the card**, greyed and sorted below the open
 *   ones. It is the record of what was done; removing it from view is how an
 *   operator ends up logging the same fix twice.
 * - **Both paragraphs are written for THIS hotel** (`problem-copy.ts`): they
 *   name its booking engine and speak about the console it actually has. The
 *   card carries `hotel` for no other reason.
 */
export function ProblemBlock({
  problem,
  steps,
  hotel,
  incident,
  planActive,
  fixReports,
  onAcceptCondition,
  onStepAction,
  stepPending,
  onLogFix,
  onAddNote,
  onEditEntry,
  onRemoveEntry,
}: {
  problem: PlanProblemView;
  steps: PlanStepView[];
  /** What measures this hotel — the engine name and the tag surface both
   *  paragraphs are composed against. */
  hotel: HotelCopyContext;
  /** Read for its OUTCOME only — `fix_applied`, `verified`, a machine close.
   *  An active incident chips nothing, so this never restates the fix plan's
   *  phase over steps that already show it. `statusClass` is what decides. */
  incident: Pick<IncidentLite, "status" | "statusClass">;
  planActive: boolean;
  /** This problem's own `fix-logged` entries, newest first. */
  fixReports: IncidentEventRow[];
  onAcceptCondition: (problem: PlanProblemView) => void;
  onStepAction: (
    step: PlanStepView,
    action: FixStepAction,
    opts?: {
      thenExecute?: boolean;
      requireNote?: boolean;
      confirmExecute?: boolean;
    },
  ) => void;
  stepPending: boolean;
  onLogFix: (problem: PlanProblemView) => void;
  onAddNote: (problem: PlanProblemView) => void;
  onEditEntry: (event: IncidentEventRow) => void;
  onRemoveEntry: (event: IncidentEventRow) => void;
}) {
  const resolved = problem.resolution !== "open";

  return (
    <div
      className={clsx(
        "rounded-lg border overflow-hidden",
        resolved
          ? "border-[#E9E9EB] bg-[#FCFCFD]"
          : "border-[#E9E9EB] bg-white shadow-sm",
      )}
    >
      {/* The header names the problem once, for both halves. */}
      <div
        className={clsx(
          "flex items-start justify-between gap-2 flex-wrap px-4 py-2.5 border-b border-[#E9E9EB]",
          resolved ? "bg-[#F7F7F9]" : "bg-[#FAFAFB]",
        )}
      >
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h4
            className={clsx(
              "text-sm font-semibold",
              resolved ? "text-gray-500" : "text-content-primary",
            )}
          >
            {problem.title}
          </h4>
          {/* The problem's own resolution first; failing that, the incident's
              status — which since 2026-08-17 chips only an OUTCOME, so an open
              problem under an active incident shows nothing and one under a
              `fix_applied` incident still says so. */}
          {problem.resolution === "fixed" ? (
            <Chip tone="green">fixed</Chip>
          ) : problem.resolution === "accepted" ? (
            <Chip tone="neutral">accepted</Chip>
          ) : (
            <IncidentStatusChip incident={incident} />
          )}
          {problem.resolution === "fixed" &&
            problem.resolvedBy?.startsWith("admin:") && (
              <OutlinedTag
                tone="neutral"
                title="Reported fixed by an operator, not observed by a Validator run. The entry reopens by itself if one of its checks fires again."
              >
                on your word
              </OutlinedTag>
            )}
          {problem.provenance === "inferred" && !resolved && (
            <OutlinedTag
              tone="orange"
              title="Inferred cause — no check on this hotel observed it; the plan rests on a variant matched from a potential finding"
            >
              cause inferred
            </OutlinedTag>
          )}
        </div>
        {/* The adjudication that says this problem will not be fixed. The two
            RECORDING acts — the fix log and the blocked-on note — sit together
            beside the plan instead, where the steps they answer are: both are
            written about the same work, and splitting them across the card had
            the operator hunting the header for the note. */}
        {planActive && !resolved && (
          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => onAcceptCondition(problem)}
              className="text-xs font-medium text-gray-500 hover:text-content-primary whitespace-nowrap"
            >
              Accept as condition…
            </button>
          </div>
        )}
      </div>

      <div
        className={clsx(
          "grid grid-cols-1 lg:grid-cols-2",
          resolved && "opacity-70",
        )}
      >
        <div className="min-w-0 px-4 py-3 lg:border-r border-[#E9E9EB]">
          <TileLabel>The problem</TileLabel>
          <ProblemBody problem={problem} hotel={hotel} />
        </div>
        <div className="min-w-0 px-4 py-3">
          <TileLabel>The fix</TileLabel>
          <ProblemFix
            problem={problem}
            steps={steps}
            hotel={hotel}
            planActive={planActive}
            onStepAction={onStepAction}
            stepPending={stepPending}
            onLogFix={onLogFix}
            onAddNote={onAddNote}
          />
          {fixReports.length > 0 && (
            <FixReports
              reports={fixReports}
              onEdit={onEditEntry}
              onRemove={onRemoveEntry}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * What THIS hotel is measured by, above the problem it has.
 *
 * It is one line and it earns its place: every sentence below it that names a
 * console — Tag Manager, the GA4 admin — was composed against this fact, and
 * an operator who cannot see the fact cannot tell a correct instruction from a
 * confident guess. It is also the fastest possible answer to "wait, does this
 * hotel even have a container?", which is the question that sent people to the
 * top of the page and back.
 *
 * It reads `engineNames`, not `engine`: this is a label, and a label can name
 * both engines of the rare hotel that books through two. The paragraphs below
 * cannot — every one of their sentences is singular — so they fall back to
 * "the booking engine" there, and the strip is what still tells the operator
 * which two.
 */
function SetupStrip({ hotel }: { hotel: HotelCopyContext }) {
  const engines = hotel.engineNames.join(", ");
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
      {engines && (
        <span
          className="text-xs font-medium text-content-secondary"
          data-tip={hotel.engineFromClaimOnly ? ENGINE_FROM_CLAIM_HINT : undefined}
        >
          {engines}
          {hotel.engineFromClaimOnly && (
            <span className="text-gray-400"> (claimed)</span>
          )}
        </span>
      )}
      {engines && <span className="text-gray-300 text-xs">·</span>}
      <span className="text-xs text-gray-500" data-tip={setupHint(hotel)}>
        {setupSummary(hotel)}
      </span>
    </div>
  );
}

/** What is wrong, and the evidence for it. */
function ProblemBody({
  problem,
  hotel,
}: {
  problem: PlanProblemView;
  hotel: HotelCopyContext;
}) {
  /**
   * The Solver writes `rootCause` as one of two things: a variant's own cause
   * prose (an inferred plan — the cause the fix acts on, which no check on
   * THIS hotel observed), or `"<checkId>: <evidence>"` restating the lead
   * finding (a strategy-backed plan). The finding panel below already carries
   * that evidence verbatim, so the second case printed the same paragraph
   * twice, back to back.
   */
  const rootCauseRestatesFinding = problem.findingEvidence.some((finding) =>
    problem.rootCause.startsWith(`${finding.checkId}: `),
  );

  const plain = problemDescription(problem.problemId, hotel);

  return (
    <>
      {/* What measures this hotel, then what this problem MEANS on it. The
          finding lines below stay the evidence; these two are the explanation
          an operator reads first, composed in the portal (not the API — the
          API ships facts, catalog titles and check evidence only). */}
      <SetupStrip hotel={hotel} />
      {plain && <p className="text-sm text-content-secondary">{plain}</p>}

      {!rootCauseRestatesFinding && (
        <div className="text-sm text-content-secondary mt-1.5">
          <span className="font-medium">Cause:</span>{" "}
          {humanizeEvidence(problem.rootCause)}
        </div>
      )}

      {/* Who resolved it, and on what basis. `resolvedBy` distinguishes an
          operator's report from a Validator run's own rollup, and the
          difference matters: only the second is an observation. */}
      {problem.resolution === "fixed" && problem.resolvedBy && (
        <div className="text-sm text-success-dark mt-1.5">
          {problem.resolvedBy.startsWith("admin:")
            ? `Reported fixed by ${fmtActor(problem.resolvedBy)}`
            : "Confirmed fixed by a Validator run"}
          {problem.resolvedAt && <> · {fmtDate(problem.resolvedAt)}</>}
        </div>
      )}

      {problem.findingEvidence.length > 0 && (
        <div className="mt-2.5 px-3 py-2.5 rounded-md bg-danger-light/60 border border-danger-light">
          {/* Measurement only. The ratification tail several checks append
              ("(For the diagnosis: …)", "Bare absence is sufficient…") is
              the check justifying itself to an engineer — an operator can't
              act on it, so it renders nowhere and survives on hover. */}
          {problem.findingEvidence.map((finding) => {
            const { head } = splitEvidence(humanizeEvidence(finding.evidence));
            return (
              <div
                key={finding.checkId}
                className="py-0.5"
                data-tip={finding.evidence}
              >
                <div className="text-sm text-danger-dark">
                  <span
                    className="font-medium"
                    data-tip={`Validator check ${finding.checkId}`}
                  >
                    {checkLabel(finding.checkId)}:
                  </span>{" "}
                  {head}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {problem.resolution === "open" && <ObservabilityBlock problem={problem} />}
    </>
  );
}

/**
 * **What would prove this problem gone** — the checks the setup check is
 * scoped to, by name.
 *
 * This is the fact that decides whether an operator's fix report can ever be
 * contradicted, and it lived only in the API until now. Two shapes, and the
 * difference between them matters more than anything else on the card:
 *
 * - **Fired checks present.** The report is provisional: any of these firing
 *   again on a later run reverts the entry to open, whoever resolved it. That
 *   is the re-fire revert, and it is the reason a claim is allowed to stand in
 *   for an observation at all (ADR 0018).
 * - **Empty scope.** The entry was minted from potential findings alone —
 *   nothing on this hotel observes the condition, so nothing can confirm the
 *   fix and nothing can overturn it. That incident's only exit is a report,
 *   which is deliberate, and the operator making the report should know they
 *   are the last word on it.
 */
function ObservabilityBlock({ problem }: { problem: PlanProblemView }) {
  const scoped = problem.firedCheckIds;
  return (
    <div className="mt-3 pt-2.5 border-t border-[#F0F0F2]">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">
        What would prove this gone
      </div>
      {scoped.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {scoped.map((checkId) => (
            <li
              key={checkId}
              className="text-xs text-content-secondary flex items-start gap-1.5"
            >
              <span
                className="w-3 h-3 mt-[3px] rounded-sm border border-dashed border-gray-300 shrink-0"
                aria-hidden
              />
              <span data-tip={`Validator check ${checkId}`}>
                {checkLabel(checkId)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-content-secondary">
          Nothing on this hotel observes this one. No check fired to find it and
          none can confirm it gone, so it clears on your report alone — and
          nothing will contradict you.
        </p>
      )}
    </div>
  );
}

/** The ordered steps that resolve this problem. */
function ProblemFix({
  problem,
  steps,
  hotel,
  planActive,
  onStepAction,
  stepPending,
  onLogFix,
  onAddNote,
}: {
  problem: PlanProblemView;
  steps: PlanStepView[];
  hotel: HotelCopyContext;
  planActive: boolean;
  onStepAction: (
    step: PlanStepView,
    action: FixStepAction,
    opts?: {
      thenExecute?: boolean;
      requireNote?: boolean;
      confirmExecute?: boolean;
    },
  ) => void;
  stepPending: boolean;
  onLogFix: (problem: PlanProblemView) => void;
  onAddNote: (problem: PlanProblemView) => void;
}) {
  const problemSteps = steps.filter((s) => s.problemId === problem.problemId);
  /**
   * The fix log lives here, on the problem it resolves, rather than once at
   * the top of the card: a fix is always logged AGAINST a problem, and a
   * single top-level button meant picking from a list of sixteen the problem
   * already on screen.
   *
   * It shows while the problem is open, steps or no steps — the case with no
   * proposed steps is exactly the one where a human did the work by hand and
   * has nothing else to record it with.
   */
  const canLogFix = planActive && problem.resolution === "open";

  /**
   * Step tags carry ONE meaning: **who carries this out**.
   *
   * They are a pair since the executor shipped (backend issue 50) — teal
   * `Autumn applies this` on the steps a button really performs, neutral
   * `By hand` on the rest. Before that there was one tag and it was amber,
   * which was wrong twice over: amber is reserved here for *waiting /
   * caution*, and "someone has to do this in a console" is an assignment,
   * not a warning. See the vocabulary module for the full reasoning.
   *
   * The per-surface labels (`In GTM`, `In Google Ads`) are still gone: where
   * the work happens is in the step's own prose, and a tag on every step made
   * the one that needs attention look like all the others.
   */

  /** The one operational instruction for this problem ON THIS HOTEL — what to
   *  do, no why (the why is the description at the top of the card). It names
   *  the hotel's own engine and its own tag surface, so it never sends an
   *  operator to a Tag Manager container the hotel has never had. */
  const solution = problemSolution(problem.problemId, hotel);

  if (problemSteps.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#E9E9EB] p-3">
        {solution && (
          <p className="text-sm text-content-primary mb-1.5">{solution}</p>
        )}
        <p className="text-xs text-gray-400">
          No steps proposed for this problem yet.
        </p>
        {canLogFix && (
          <ProblemActions
            problem={problem}
            onLogFix={onLogFix}
            onAddNote={onAddNote}
            className="mt-2"
          />
        )}
      </div>
    );
  }

  return (
    <div>
      {solution && (
        <p className="text-sm text-content-primary mb-2.5">{solution}</p>
      )}
      <ol className="flex flex-col gap-2.5">
        {problemSteps.map((step) => (
          <li key={step.index} className="flex items-start gap-2.5">
            <span className="text-xs text-gray-400 font-medium mt-0.5 w-4 shrink-0">
              {step.index + 1}.
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Stored kind, deliberately: the server validates actions
                    against the plan's own step, and a catalog edit can
                    re-point `resolvedKind` at a different step's kind —
                    labeling by it once offered Approve on a manual step
                    (409, 2026-08-15).

                    The tag reads off `canExecute`, not off the kind: an
                    automatic step whose surface is the customer's, or that
                    nothing implements, is by hand in every sense that
                    matters to the person reading it. Kind is a catalog fact;
                    this is a fact about this hotel. */}
                <StepOwnerTag step={step} />
                {step.description ? (
                  <span className="text-sm text-content-primary">
                    {step.description}
                  </span>
                ) : (
                  /* The stored ref no longer resolves — a catalog edit
                     removed or moved the step it pointed at. The raw ref is
                     engineer material (hover), never operator copy. */
                  <span
                    className="text-sm text-content-secondary italic"
                    data-tip={step.catalogRef}
                  >
                    This step came from an older version of the playbook and
                    no longer applies — mark it Not needed.
                  </span>
                )}
              </div>
              {/* Why this one is by hand, from the server — but only where
                  it is not obvious. A manual step is by hand by definition
                  and saying so twice is noise; an AUTOMATIC step that cannot
                  be carried out here is the surprising case, and the reason
                  ("the container is the customer's", "no repair has been
                  built") is what stops an operator hunting for a button that
                  is not there. */}
              {!isStepComplete(step.status) &&
                step.kind === "automatic" &&
                !step.canExecute.executable &&
                step.canExecute.reason && (
                  <div className="text-xs text-content-secondary mt-1">
                    {step.canExecute.reason}
                  </div>
                )}
              {step.note && (
                <div className="text-xs text-gray-500 mt-1">{step.note}</div>
              )}
              {/* What a machine execution wrote, in its own words. It is not
                  the step's note — nobody typed it — so it does not render as
                  one. */}
              {step.execution?.mode === "machine" && (
                <div className="text-xs text-brand-primary mt-1">
                  {stepExecutionSummary(step.execution)}
                </div>
              )}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <StepStatusAndActions
                  step={step}
                  planActive={planActive}
                  pending={stepPending}
                  onAction={onStepAction}
                />
              </div>
              {/* No deliverable footnote here: both investigation steps in
                  the catalog (P3, P12 — the only two carriers, ADR 0015)
                  name their own deliverable in the step prose above, so a
                  fixed line restated it a second time. The catalog is the
                  single source of a step's words. */}
            </div>
          </li>
        ))}
      </ol>
      {canLogFix && (
        <div className="mt-3 pt-3 border-t border-[#E9E9EB]">
          {/* Working the steps is the ordinary route now, so the fix log is
              the SECONDARY affordance here — for the fix that happened
              outside the plan, or a plan whose steps do not describe what was
              actually done. It stays a plain link rather than a second solid
              button competing with the step's own. */}
          <ProblemActions
            problem={problem}
            onLogFix={onLogFix}
            onAddNote={onAddNote}
          />
          <p className="text-[11px] text-gray-400 mt-1.5">
            Or record a fix that did not follow these steps.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The operator's own reports against THIS problem.
 *
 * They used to render in the incident's Blocked-on list, because the server
 * writes a fix report and a blocked-on note from the same gesture. That put
 * them under a heading meaning the opposite of what they say: a fix report is
 * the record that something was DONE, not that the incident is waiting on it.
 * They belong to the problem they name.
 */
function FixReports({
  reports,
  onEdit,
  onRemove,
}: {
  reports: IncidentEventRow[];
  onEdit: (event: IncidentEventRow) => void;
  onRemove: (event: IncidentEventRow) => void;
}) {
  return (
    <div className="mt-3 pt-3 border-t border-[#E9E9EB]">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">
        Reported fixed
      </div>
      <div className="flex flex-col">
        {reports.map((event) => (
          <div
            key={event.id}
            className="group py-1.5 border-b border-[#F0F0F2] last:border-b-0"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm text-content-secondary min-w-0">
                {event.reason}
              </span>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity shrink-0">
                <button
                  onClick={() => onEdit(event)}
                  className="px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-500 hover:text-content-primary hover:bg-gray-100"
                  data-tip="Correct the wording — nothing re-runs, the reminder clock does not move"
                >
                  Edit
                </button>
                <button
                  onClick={() => onRemove(event)}
                  className="px-1.5 py-0.5 text-[11px] font-medium rounded text-gray-400 hover:text-danger-dark hover:bg-danger-light/50"
                  data-tip="Hide this row from the history (it changes nothing else)"
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {fmtDate(event.at)} · {fmtActor(event.actor)}
              {event.editedAt && (
                <span
                  data-tip={`edited by ${fmtActor(event.editedBy) || "an admin"}`}
                >
                  {" "}
                  · edited
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The two things an operator RECORDS against a problem, side by side.
 *
 * They are the same gesture on the server and the opposite fact to the reader:
 * a fix log says the work is done and resolves the entry; a blocked-on note
 * says it is not and resolves nothing — it only restarts the 7-day reminder.
 * Pairing them is what makes the choice legible; the note sat in the card
 * header until 2026-08-14, a column away from the steps it answers.
 */
function ProblemActions({
  problem,
  onLogFix,
  onAddNote,
  className = "",
}: {
  problem: PlanProblemView;
  onLogFix: (problem: PlanProblemView) => void;
  onAddNote: (problem: PlanProblemView) => void;
  className?: string;
}) {
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      <button
        onClick={() => onLogFix(problem)}
        className="px-3 py-1.5 text-xs font-semibold rounded-md border border-brand-primary text-brand-primary bg-white hover:bg-[#E7F1F3]"
      >
        Log fix for this problem…
      </button>
      <button
        onClick={() => onAddNote(problem)}
        data-tip="Record what this problem is waiting on — it groups under this problem in the blocked-on log and resolves nothing"
        className="px-3 py-1.5 text-xs font-medium rounded-md border border-[#E9E9EB] text-content-secondary bg-white hover:bg-gray-50"
      >
        Log Blocked-on Note
      </button>
    </div>
  );
}

/**
 * Who carries this step out — the pair of tags, in the palette's reserved
 * meanings (teal = "our fix in flight", neutral = "facts & gates").
 *
 * It reads `canExecute`, which is a fact about THIS hotel, rather than the
 * step's catalog `kind`. On a hotel whose container is the customer's, an
 * automatic GTM step is by hand in every sense the operator cares about, and
 * a tag claiming otherwise would be the most expensive kind of wrong here.
 *
 * A completed step gets no tag: it is finished, and how it was carried out is
 * already stated underneath in the words of whoever (or whatever) did it.
 */
function StepOwnerTag({ step }: { step: PlanStepView }) {
  if (isStepComplete(step.status)) return null;
  if (step.canExecute.executable) {
    return (
      <span
        data-tip={MACHINE_STEP_HINT}
        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#E7F1F3] text-brand-primary whitespace-nowrap"
      >
        {MACHINE_STEP_TAG}
      </span>
    );
  }
  return (
    <span
      data-tip={MANUAL_STEP_HINT}
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#F4F4F6] text-gray-600 border border-[#E9E9EB] whitespace-nowrap"
    >
      {MANUAL_STEP_TAG}
    </span>
  );
}

function StepStatusAndActions({
  step,
  planActive,
  pending,
  onAction,
}: {
  step: PlanStepView;
  planActive: boolean;
  pending: boolean;
  onAction: (
    step: PlanStepView,
    action: FixStepAction,
    opts?: {
      thenExecute?: boolean;
      requireNote?: boolean;
      confirmExecute?: boolean;
    },
  ) => void;
}) {
  // Stored kind, never `resolvedKind`: the server refuses actions against
  // anything but the plan's own step kind, and a catalog edit re-points
  // `resolvedKind` by array position. Preferring it here rendered "I've done
  // this" (approve) on a stored-manual step — 409 "approval is the
  // automatic-step gate" — observed live 2026-08-15.
  const kind = step.kind;
  const done = isStepComplete(step.status);

  if (done) {
    /**
     * It read `marked applied ✓` until 2026-08-14, which is a claim about the
     * ACCOUNT — and back then no click here changed one. Since the executor
     * shipped some clicks do, so the two cases are now told apart rather than
     * flattened: `applied` for a change this system made, `recorded` for a
     * person's report of one. `fmtActor` shortens `admin:x@…` to `x`.
     *
     * Neither is proof. The hover says so in both cases, because the thing
     * that confirms a fix landed is the next Validator run, not this row.
     */
    const machine = step.execution?.mode === "machine";
    const label =
      step.status === "skipped" ? "skipped" : machine ? "applied" : "recorded";
    return (
      <span
        className="text-xs text-success-dark"
        data-tip={
          step.status === "skipped"
            ? undefined
            : machine
              ? "Carried out from here against the live account. The next Validator run is what confirms it took effect."
              : "Someone recorded this as done — nothing here checked it. The next Validator run is what confirms it landed."
        }
      >
        {label}
        {step.statusChangedBy && <> by {fmtActor(step.statusChangedBy)}</>}
        {step.statusChangedAt && <> · {fmtDate(step.statusChangedAt)}</>} ✓
      </span>
    );
  }
  if (!planActive) {
    return <span className="text-xs text-gray-400">{step.status}</span>;
  }

  const smallBtn =
    "px-2.5 py-1 text-xs font-medium rounded border border-[#E9E9EB] text-content-secondary hover:bg-gray-50 disabled:opacity-50";

  if (kind === "automatic") {
    /**
     * An automatic step has TWO routes now, and which one shows is decided by
     * the server, per hotel (backend issue 50).
     *
     * **Apply this change** — the step is bound to an executor and this
     * hotel's surface is ours, so the click really does it: approve, then
     * execute, then the account is different. It goes through a confirmation
     * naming the change, because it is the only gesture in this dashboard that
     * leaves the building.
     *
     * **I did this myself…** — everything else: the step nothing implements
     * yet (most of the catalog), or a surface that belongs to the customer's
     * console. Same terminal status, a mandatory note, and an execution record
     * saying a person did it. This is what the single button used to be for
     * every step, back when the whole module was read-only against Google.
     *
     * The by-hand route stays available even when the machine one is, and that
     * is deliberate: an operator who has already made the change in the UI
     * must be able to say so without the system writing it a second time.
     *
     * ⚠️ **Still owed.** Closing the LAST open step on a problem resolves it
     * on the operator's word, which resets the trusted data start date and
     * drops the hotel to Monitoring. Nothing on this row says so, by either
     * route. A comment here once claimed a `oneStepLeft` warning did — it does
     * not exist anywhere in `src` (checked 2026-08-15). The confirmation below
     * covers the machine route; the by-hand route still needs it.
     */
    if (step.status === "pending" || step.status === "approved") {
      const executable = step.canExecute.executable;
      return (
        <>
          {executable && (
            <button
              disabled={pending}
              onClick={() =>
                step.status === "pending"
                  ? onAction(step, "approve", {
                      thenExecute: true,
                      confirmExecute: true,
                    })
                  : onAction(step, "execute", { confirmExecute: true })
              }
              data-tip={step.canExecute.summary ?? undefined}
              className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary rounded hover:bg-brand-primary-hover disabled:opacity-50"
            >
              Apply this change
            </button>
          )}
          <button
            disabled={pending}
            onClick={() => onAction(step, "complete", { requireNote: true })}
            data-tip="Record that you made this change yourself, and what you changed."
            className={
              executable
                ? smallBtn
                : "px-3 py-1.5 text-xs font-medium text-white bg-brand-primary rounded hover:bg-brand-primary-hover disabled:opacity-50"
            }
          >
            I did this myself…
          </button>
          <button
            disabled={pending}
            onClick={() => onAction(step, "skip", { requireNote: true })}
            data-tip="Record that this step will not be done, and why."
            className={smallBtn}
          >
            Not needed…
          </button>
        </>
      );
    }
  }

  /**
   * `pending` was printed here as raw status text beside the buttons, which
   * said nothing an open step with buttons does not already say. A status
   * shows only once it carries news — `issued` means we are waiting on
   * somebody, and that is worth a line.
   */
  return (
    <>
      {step.status !== "pending" && (
        <span className="text-xs text-gray-400">
          {step.status === "issued" ? "waiting on them" : step.status}
        </span>
      )}
      {/* No "I've asked them…" (issue) button since 2026-08-15: the
          operator performs manual steps themselves more often than they
          hand one off, and a blocked hand-off is what the Log Blocked-on
          Note is for. The server's `issue` action still exists — this
          surface just stopped offering it. */}
      <button
        disabled={pending}
        onClick={() => onAction(step, "complete", { requireNote: true })}
        data-tip="Record that this step is done, and what was done."
        className={smallBtn}
      >
        It&rsquo;s done…
      </button>
      <button
        disabled={pending}
        onClick={() => onAction(step, "skip", { requireNote: true })}
        data-tip="Record that this step will not be done, and why."
        className={smallBtn}
      >
        Not needed…
      </button>
    </>
  );
}
